import React, { useState, useCallback, useMemo, memo } from 'react';
import { PasskeyLoginStep } from '../components/PasskeyLoginStep';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, User, Building2, FileText, Briefcase } from 'lucide-react';
import { TermsAndConditionsModal } from '../components/TermsAndConditionsModal';

// Memoized password toggle button for better performance
const PasswordToggle = memo(({ show, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
    tabIndex={-1}
  >
    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
  </button>
));

export const LoginPage = memo(() => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA states
  const [requires2FA, setRequires2FA] = useState(false);
  const [emailMasked, setEmailMasked] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [codeRequested, setCodeRequested] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);
  const [passkey, setPasskey] = useState(null);

  // Optimized countdown timer
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Memoized toggle handler
  const togglePassword = useCallback(() => setShowPassword(prev => !prev), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      let response;
      let data;
      
      // Try new 2FA flow endpoint first
      try {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check-credentials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
      } catch (networkError) {
        console.error('Network error on check-credentials, trying legacy login:', networkError);
        // Network error - try legacy endpoint
        response = null;
      }
      
      // Fallback to old login endpoint if new one fails (404, network error, or not available)
      if (!response || response.status === 404) {
        console.log('Falling back to legacy login endpoint');
        try {
          response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });
        } catch (legacyNetworkError) {
          console.error('Network error on legacy login:', legacyNetworkError);
          throw new Error('Unable to connect to server. Please check your internet connection.');
        }
        
        data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Login failed');
        }
        
        // SECURITY: the legacy /auth/login endpoint also returns a 2FA challenge
        // (NO access_token) for regular USERs. Route through 2FA — never create a
        // session with an undefined token.
        if (data.requires_passkey) {
          setPasskey(data);
          return;
        }
        if (data.requires_2fa || !data.access_token) {
          setRequires2FA(true);
          setEmailMasked(data.email_masked);
          setCodeRequested(true);
          setCountdown(0);
          return;
        }
        
        // Legacy login returned tokens directly (admin / compliance only).
        const user = await login(email, password, data);
        toast.success(t('loginSuccess'));
        
        setTimeout(() => {
          if (user.role === 'ADMIN' || user.role === 'COMPLIANCE') {
            navigate('/admin');
          } else if (user.role === 'COMMERCIAL_OFFICER') {
            navigate('/co');
          } else {
            navigate('/dashboard');
          }
        }, 100);
        return;
      }
      
      data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Check if 2FA is required or direct login (admin bypass)
      if (data.requires_passkey) {
        setPasskey(data);
      } else if (data.requires_2fa) {
        setRequires2FA(true);
        setEmailMasked(data.email_masked);
        setCodeRequested(false);
        setCountdown(0);
      } else if (data.access_token) {
        // Direct login (2FA bypassed for admins)
        const user = await login(email, password, data);
        toast.success(t('loginSuccess'));
        
        setTimeout(() => {
          if (user.role === 'ADMIN' || user.role === 'COMPLIANCE') {
            navigate('/admin');
          } else if (user.role === 'COMMERCIAL_OFFICER') {
            navigate('/co');
          } else {
            navigate('/dashboard');
          }
        }, 100);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || t('invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async () => {
    setRequestingCode(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/send-2fa-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send code');
      }
      
      setCodeRequested(true);
      setCountdown(60);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRequestingCode(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      // Login successful - use the login function from context
      const user = await login(email, password, data);
      toast.success(t('loginSuccess'));
      
      setTimeout(() => {
        if (user.role === 'ADMIN' || user.role === 'COMPLIANCE') {
          navigate('/admin');
        } else if (user.role === 'COMMERCIAL_OFFICER') {
          navigate('/co');
        } else {
          navigate('/dashboard');
        }
      }, 100);
    } catch (error) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setResending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/send-2fa-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }
      
      toast.success('New verification code sent');
      setCountdown(60);
      setVerificationCode('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setVerificationCode('');
    setCountdown(0);
    setCodeRequested(false);
  };

  const handlePasskeySuccess = async (tokenData) => {
    const user = await login(email, password, tokenData);
    toast.success(t('loginSuccess'));
    navigate(user.role === 'COMMERCIAL_OFFICER' ? '/co' : '/dashboard');
  };

  if (passkey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md" data-testid="passkey-card">
          <CardContent className="pt-6">
            <PasskeyLoginStep
              loginTx={passkey.login_tx}
              enrolled={passkey.passkey_enrolled}
                  email={email}
              onSuccess={handlePasskeySuccess}
              onCancel={() => { setPasskey(null); setPassword(''); }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen flex flex-col bg-muted/30">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                  alt="Youse Capital" 
                  className="h-[168px] w-auto"
                />
              </Link>
            </div>
            
            <Card data-testid="2fa-card">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-[#1a365d]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#1a365d]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <CardTitle>Two-Factor Authentication</CardTitle>
                <CardDescription>
                  {codeRequested 
                    ? <>We've sent a verification code to <strong>{emailMasked}</strong></>
                    : <>For your security, we need to verify your identity</>
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!codeRequested ? (
                  // Step 1: Request code button
                  <div className="space-y-6">
                    <div className="text-center py-4">
                      <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Click the button below to receive a verification code at:
                      </p>
                      <p className="font-medium text-[#1a365d]">{emailMasked}</p>
                    </div>
                    
                    <Button 
                      onClick={handleRequestCode} 
                      className="w-full bg-[#1a365d] hover:bg-[#2d4a7c]" 
                      disabled={requestingCode}
                      data-testid="request-code-btn"
                    >
                      {requestingCode ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Request Code by Email
                        </>
                      )}
                    </Button>
                    
                    <div className="pt-4 border-t text-center">
                      <Button variant="link" onClick={handleBack} className="text-muted-foreground">
                        ← Back to Login
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Step 2: Enter code
                  <div className="space-y-4">
                    {/* Countdown Timer */}
                    {countdown > 0 && (
                      <div className="text-center py-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                          Time remaining: <span className="font-mono font-bold">{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</span>
                        </p>
                      </div>
                    )}
                    
                    <form onSubmit={handleVerify2FA} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="code">Verification Code</Label>
                        <Input
                          id="code"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          placeholder="000000"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="text-center text-2xl tracking-[0.5em] font-mono"
                          data-testid="2fa-code"
                          autoFocus
                          required
                        />
                        <p className="text-xs text-muted-foreground text-center">
                          Enter the 6-digit code from your email
                        </p>
                      </div>
                      
                      <Button type="submit" className="w-full" disabled={loading || verificationCode.length !== 6} data-testid="2fa-submit">
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify & Login'
                        )}
                      </Button>
                    </form>
                    
                    {/* Resend Section */}
                    <div className="mt-6 pt-4 border-t">
                      <p className="text-sm text-center text-muted-foreground mb-3">
                        Didn't receive the code?
                      </p>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={handleResend}
                        disabled={resending || countdown > 0}
                      >
                        {resending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : countdown > 0 ? (
                          `Send Again (${countdown}s)`
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Send Again
                          </>
                        )}
                      </Button>
                    </div>
                    
                    <div className="pt-2 text-center">
                      <Button variant="link" onClick={handleBack} className="text-muted-foreground">
                        ← Back to Login
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                alt="Youse Capital" 
                className="h-[168px] w-auto"
              />
            </Link>
          </div>
          
          <Card data-testid="login-card">
            <CardHeader>
              <CardTitle>{t('login')}</CardTitle>
              <CardDescription>Enter your credentials to access your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    {t('forgotPassword')}
                  </Link>
                </div>
                
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('login')
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">{t('dontHaveAccount')} </span>
                <Link to="/signup" className="text-primary hover:underline font-medium" data-testid="signup-link">
                  {t('signup')}
                </Link>
              </div>
            </CardContent>
          </Card>
          
          <p className="text-xs text-center text-muted-foreground mt-6 px-4">
            {t('disclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
});

export const SignupPage = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    account_type: 'PERSONAL',
    company_name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('At least one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('At least one number');
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'password') {
      setPasswordErrors(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      toast.error('Password does not meet requirements');
      return;
    }
    
    if (formData.account_type === 'BUSINESS' && !formData.company_name.trim()) {
      toast.error('Company name is required for Business accounts');
      return;
    }

    // Show terms modal if not yet accepted
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }
    
    await submitRegistration();
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    // Auto-submit after accepting terms
    submitRegistration();
  };

  const submitRegistration = async () => {
    setLoading(true);
    try {
      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        account_type: formData.account_type,
        company_name: (formData.account_type === 'BUSINESS' || formData.account_type === 'SINGLE') ? (formData.company_name.trim() || null) : null,
        referral_code: referralCode || null,
        terms_accepted: true
      });
      toast.success('Application submitted successfully!');
      // Redirect to application submitted page instead of account setup
      navigate('/application-submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                alt="Youse Capital" 
                className="h-[168px] w-auto"
              />
            </Link>
          </div>
          
          <Card data-testid="signup-card">
            <CardHeader>
              <CardTitle>{t('createAccount')}</CardTitle>
              <CardDescription>Start your journey with Youse Capital</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Account Type Selection */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Account Type</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Personal Account */}
                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.account_type === 'PERSONAL'
                          ? 'border-[#1a365d] bg-[#1a365d]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setFormData({ ...formData, account_type: 'PERSONAL', company_name: '' })}
                      data-testid="account-type-personal"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.account_type === 'PERSONAL' ? 'bg-[#1a365d] text-white' : 'bg-gray-100'
                        }`}>
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Personal Account</p>
                          <p className="text-xs text-gray-500">For individuals</p>
                        </div>
                      </div>
                      <p className="mt-2 text-sm font-medium text-[#1a365d]">Setup: $150,000</p>
                    </div>
                    
                    {/* Business Account */}
                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.account_type === 'BUSINESS'
                          ? 'border-[#1a365d] bg-[#1a365d]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setFormData({ ...formData, account_type: 'BUSINESS' })}
                      data-testid="account-type-business"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.account_type === 'BUSINESS' ? 'bg-[#1a365d] text-white' : 'bg-gray-100'
                        }`}>
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Business Account</p>
                          <p className="text-xs text-gray-500">For companies</p>
                        </div>
                      </div>
                      <p className="mt-2 text-sm font-medium text-[#1a365d]">Setup: $250,000</p>
                    </div>
                    
                    {/* Single Account */}
                    <div
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.account_type === 'SINGLE'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setFormData({ ...formData, account_type: 'SINGLE' })}
                      data-testid="account-type-single"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          formData.account_type === 'SINGLE' ? 'bg-amber-500 text-white' : 'bg-gray-100'
                        }`}>
                          <Briefcase className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Single Account</p>
                          <p className="text-xs text-green-600 font-medium">Free setup · Moringa access</p>
                        </div>
                      </div>
                      <p className="mt-2 text-sm font-bold text-green-600">Setup: FREE</p>
                      <p className="text-[10px] text-gray-400 mt-1">Max $100K/week transactions</p>
                    </div>
                  </div>
                </div>

                {/* Company Name (for Business or Single) */}
                {(formData.account_type === 'BUSINESS' || formData.account_type === 'SINGLE') && (
                  <div className="space-y-2">
                    <Label htmlFor="company_name">
                      {formData.account_type === 'SINGLE' ? 'Company Name (optional)' : 'Company Name *'}
                    </Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      placeholder="Your Company LLC"
                      value={formData.company_name}
                      onChange={handleChange}
                      data-testid="signup-company"
                      required={formData.account_type === 'BUSINESS'}
                    />
                    {formData.account_type === 'SINGLE' && (
                      <p className="text-xs text-green-600">
                        Single Account has <span className="font-semibold">FREE setup</span> and unlocks access to <span className="font-semibold">Moringa Agro Invest</span>.
                        Weekly limits: $100,000 transactions, $1,000,000 deposits.
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">{t('firstName')}</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleChange}
                      data-testid="signup-firstname"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">{t('lastName')}</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={handleChange}
                      data-testid="signup-lastname"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="signup-email"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      data-testid="signup-password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Password Requirements */}
                  {formData.password && (
                    <div className="mt-2 p-3 bg-muted/50 rounded-lg text-xs space-y-1">
                      <p className="font-medium text-muted-foreground mb-2">Password requirements:</p>
                      <div className={`flex items-center gap-2 ${formData.password.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {formData.password.length >= 8 ? '✓' : '○'} At least 8 characters
                      </div>
                      <div className={`flex items-center gap-2 ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {/[A-Z]/.test(formData.password) ? '✓' : '○'} At least one uppercase letter (A-Z)
                      </div>
                      <div className={`flex items-center gap-2 ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {/[a-z]/.test(formData.password) ? '✓' : '○'} At least one lowercase letter (a-z)
                      </div>
                      <div className={`flex items-center gap-2 ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {/[0-9]/.test(formData.password) ? '✓' : '○'} At least one number (0-9)
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    data-testid="signup-confirm-password"
                    required
                  />
                </div>
                
                {/* Terms acceptance indicator */}
                {termsAccepted && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    <FileText className="w-4 h-4" />
                    <span>Terms and Conditions accepted</span>
                  </div>
                )}
                
                <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      {termsAccepted ? t('createAccount') : 'Review Terms & Create Account'}
                    </>
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">{t('alreadyHaveAccount')} </span>
                <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                  {t('login')}
                </Link>
              </div>
            </CardContent>
          </Card>
          
          <p className="text-xs text-center text-muted-foreground mt-6 px-4">
            {t('disclaimer')}
          </p>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
        accountType={formData.account_type}
      />
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setSent(true);
        toast.success('If an account exists with this email, you will receive a reset link.');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                alt="Youse Capital" 
                className="h-[168px] w-auto"
              />
            </Link>
          </div>
          
          <Card data-testid="forgot-password-card">
            <CardHeader>
              <CardTitle>{t('resetPassword')}</CardTitle>
              <CardDescription>Enter your email to receive a password reset link</CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium mb-2">Check your email</h3>
                  <p className="text-muted-foreground text-sm">
                    If an account exists with this email, you will receive a password reset link shortly.
                  </p>
                  <Link to="/login" className="inline-block mt-4">
                    <Button variant="outline">{t('back')} to {t('login')}</Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email"
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('loading')}
                      </>
                    ) : (
                      t('resetPassword')
                    )}
                  </Button>
                </form>
              )}
              
              <div className="mt-6 text-center text-sm">
                <Link to="/login" className="text-primary hover:underline font-medium">
                  {t('back')} to {t('login')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [error, setError] = useState('');

  React.useEffect(() => {
    // Verify token on mount
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setError('No reset token provided');
        return;
      }
      
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-reset-token?token=${token}`);
        const data = await response.json();
        setTokenValid(data.valid);
        if (!data.valid) {
          setError(data.message || 'Invalid or expired token');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Could not verify token');
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d3a7cf2-25a3-4666-9f30-7291006a257d/artifacts/obfdqs4z_ChatGPT%20Image%2010%20de%20fev.%20de%202026%2C%2002_11_06.png" 
                alt="Youse Capital" 
                className="h-[168px] w-auto"
              />
            </Link>
          </div>
          
          <Card data-testid="reset-password-card">
            <CardHeader>
              <CardTitle>{t('resetPassword')}</CardTitle>
              <CardDescription>Create a new password for your account</CardDescription>
            </CardHeader>
            <CardContent>
              {tokenValid === null ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                  <p className="text-muted-foreground mt-2">Verifying reset token...</p>
                </div>
              ) : tokenValid === false ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium mb-2 text-red-600">Invalid Reset Link</h3>
                  <p className="text-muted-foreground text-sm mb-4">{error}</p>
                  <p className="text-muted-foreground text-sm">
                    The reset link may have expired or already been used. Please request a new one.
                  </p>
                  <Link to="/forgot-password" className="inline-block mt-4">
                    <Button>Request New Reset Link</Button>
                  </Link>
                </div>
              ) : success ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium mb-2">Password Reset Complete!</h3>
                  <p className="text-muted-foreground text-sm">
                    Your password has been updated successfully. You will be redirected to login...
                  </p>
                  <Link to="/login" className="inline-block mt-4">
                    <Button>Go to Login</Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('newPassword')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="reset-password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="reset-confirm-password"
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('loading')}
                      </>
                    ) : (
                      'Set New Password'
                    )}
                  </Button>
                </form>
              )}
              
              <div className="mt-6 text-center text-sm">
                <Link to="/login" className="text-primary hover:underline font-medium">
                  {t('back')} to {t('login')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
